import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Storefront, GasPump, Wrench, ShoppingBag, Tire, DotsThreeCircle } from "@phosphor-icons/react";
import { BR_STATES } from "@/lib/br";
import { CityAutocomplete } from "@/components/CityAutocomplete";

const TYPES = [
  { value: "posto", label: "Postos de combustível", Icon: GasPump, color: "text-blue-600" },
  { value: "oficina", label: "Oficinas mecânicas", Icon: Wrench, color: "text-amber-600" },
  { value: "autopecas", label: "Autopeças", Icon: ShoppingBag, color: "text-purple-600" },
  { value: "pneus", label: "Pneus", Icon: Tire, color: "text-slate-700" },
  { value: "outros", label: "Outros", Icon: DotsThreeCircle, color: "text-slate-500" },
];
const EMPTY = { name: "", type: "posto", cnpj: "", city: "", state: "", phone: "", address: "", notes: "" };

export default function Suppliers() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("posto");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const { data } = await api.get("/suppliers");
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm({ ...EMPTY, type: tab }); setEditingId(null); setOpen(true); };
  const startEdit = (s) => { setForm({ ...s }); setEditingId(s.id); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/suppliers/${editingId}`, form);
        toast.success("Fornecedor atualizado");
      } else {
        await api.post("/suppliers", form);
        toast.success("Fornecedor cadastrado");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este fornecedor? Os registros de abastecimento/despesa ligados permanecem.")) return;
    await api.delete(`/suppliers/${id}`);
    toast.success("Removido");
    load();
  };

  const filtered = items.filter((i) => (i.type || "posto") === tab);
  const countByType = TYPES.reduce((a, t) => ({ ...a, [t.value]: items.filter(i => (i.type || "posto") === t.value).length }), {});

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope" }}>
          <Storefront size={32} weight="duotone" className="text-blue-600" /> Fornecedores
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Postos, oficinas, autopeças e outros fornecedores cadastrados</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-auto p-1 flex-wrap">
          {TYPES.map((t) => {
            const Icon = t.Icon;
            return (
              <TabsTrigger key={t.value} value={t.value} data-testid={`sup-tab-${t.value}`} className="py-2 px-4 gap-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                <Icon size={16} /> {t.label} <span className="ml-1 opacity-70 text-xs">({countByType[t.value] || 0})</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {TYPES.map((t) => (
          <TabsContent key={t.value} value={t.value}>
            <div className="flex justify-end mb-3">
              <Button onClick={startNew} data-testid={`btn-new-supplier-${t.value}`} className="bg-blue-600 hover:bg-blue-700 hover:text-white text-white gap-1.5">
                <Plus size={16} /> Novo {t.label.replace(/s$/, "").toLowerCase()}
              </Button>
            </div>
            <Card>
              <CardHeader><CardTitle className="text-lg flex items-center gap-2"><t.Icon size={20} className={t.color} /> {t.label} ({filtered.length})</CardTitle></CardHeader>
              <CardContent>
                {filtered.length === 0 ? (
                  <p className="text-muted-foreground text-sm italic">Nenhum {t.label.toLowerCase()} cadastrado ainda. Clique em <strong>+ Novo</strong> pra começar.</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>CNPJ</TableHead><TableHead>Cidade/UF</TableHead><TableHead>Telefone</TableHead><TableHead>Endereço</TableHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>
                      {filtered.map((s) => (
                        <TableRow key={s.id} data-testid={`sup-row-${s.id}`}>
                          <TableCell className="font-semibold">{s.name}</TableCell>
                          <TableCell className="text-xs font-mono text-slate-500">{s.cnpj || "-"}</TableCell>
                          <TableCell className="text-sm">{s.city ? `${s.city}/${s.state}` : "-"}</TableCell>
                          <TableCell className="text-sm">{s.phone || "-"}</TableCell>
                          <TableCell className="text-xs text-slate-500 max-w-[240px] truncate">{s.address || "-"}</TableCell>
                          <TableCell className="text-right space-x-1">
                            <Button size="icon" variant="ghost" onClick={() => startEdit(s)} data-testid={`btn-edit-sup-${s.id}`}><PencilSimple size={16} className="text-blue-600" /></Button>
                            <Button size="icon" variant="ghost" onClick={() => remove(s.id)} data-testid={`btn-del-sup-${s.id}`}><Trash size={16} className="text-red-600" /></Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Editar" : "Novo"} fornecedor</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="sup-name" placeholder="Ex: Auto Posto Petrobras Trevão" />
            </div>
            <div><Label>Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="sup-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>CNPJ</Label>
              <Input value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} data-testid="sup-cnpj" placeholder="00.000.000/0000-00" />
            </div>
            <div><Label>UF</Label>
              <Select value={form.state} onValueChange={(v) => setForm({ ...form, state: v })}>
                <SelectTrigger data-testid="sup-state"><SelectValue placeholder="UF" /></SelectTrigger>
                <SelectContent>{BR_STATES.map(([uf]) => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Cidade</Label>
              <CityAutocomplete value={form.city} uf={form.state} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Digite a cidade" data-testid="sup-city" />
            </div>
            <div className="col-span-2"><Label>Endereço</Label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="sup-address" placeholder="Ex: BR-116 km 235, Bairro Industrial" />
            </div>
            <div><Label>Telefone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="sup-phone" placeholder="(11) 99999-9999" />
            </div>
            <div className="col-span-2"><Label>Observações</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="sup-notes" placeholder="Ex: 5% de desconto na carta frete" />
            </div>
            <DialogFooter className="col-span-2">
              <Button type="submit" data-testid="btn-save-sup" className="bg-blue-600 hover:bg-blue-700 hover:text-white text-white">Salvar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
